import { useEffect, useState } from 'react';
import {
  Calculator,
  FileText,
  TrendingUp,
  TrendingDown,
  Wallet,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  CheckCircle2,
  ArrowRight,
  Scale
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import { transactionAPI } from '../../services/api';
import { formatPercentage } from '../../utils/formatters';
import {
  calculateTaxReport,
  calculateAdvanceTaxSchedule
} from '../../utils/taxCalculator';
import LoadingSpinner from '../common/LoadingSpinner';

export default function TaxCalculator() {
  const { user } = useAuth();
  const { formatAmount, config } = useConfig();
  const [loading, setLoading] = useState(true);
  const [taxData, setTaxData] = useState(null);
  const [expenseData, setExpenseData] = useState(null);
  const [taxReport, setTaxReport] = useState(null);
  const [advanceTax, setAdvanceTax] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('recommended'); // 'recommended', 'presumptive', 'actual'
  const [expandedSections, setExpandedSections] = useState({
    comparison: true,
    income: true,
    expenses: false,
    tax: true,
    tds: false,
    advance: true
  });

  useEffect(() => {
    loadTaxData();
  }, [user?.financialYear]);

  const loadTaxData = async () => {
    try {
      setLoading(true);
      const { data } = await transactionAPI.getTaxData(user?.financialYear || '2025-26');
      setTaxData(data.data.incomeByCategory);
      setExpenseData(data.data.expenseByCategory || {});

      // Calculate tax report with both income and expense data
      const report = calculateTaxReport(data.data.incomeByCategory, data.data.expenseByCategory || {});
      setTaxReport(report);

      // Calculate advance tax schedule based on recommended method
      const advance = calculateAdvanceTaxSchedule(
        report.summary.totalTaxLiability,
        report.summary.totalTDSPaid
      );
      setAdvanceTax(advance);
    } catch (error) {
      console.error('Failed to load tax data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Get the currently selected method's data
  const getSelectedMethodData = () => {
    if (!taxReport) return null;

    if (selectedMethod === 'presumptive' || (selectedMethod === 'recommended' && taxReport.comparison.presumptiveBetter)) {
      return taxReport.presumptiveMethod;
    }
    return taxReport.actualMethod;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!taxReport) {
    return (
      <div className="card text-center py-12">
        <AlertCircle className="w-12 h-12 text-surface-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-surface-900 mb-2">
          No income data available
        </h3>
        <p className="text-surface-500">
          Add some income transactions to see your tax calculations.
        </p>
      </div>
    );
  }

  const { income, expenses, presumptiveMethod, actualMethod, comparison, tds, summary } = taxReport;
  const currentMethod = getSelectedMethodData();
  const hasExpenses = expenses.actual > 0;

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Tax Report</h1>
          <p className="page-subtitle">
            Indian New Tax Regime - FY {user?.financialYear || '2025-26'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium"
            style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
          >
            <Calculator className="w-4 h-4 mr-1.5" />
            {summary.itrForm}
          </span>
        </div>
      </div>

      {/* Method Comparison Card */}
      {hasExpenses && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <button
            onClick={() => toggleSection('comparison')}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <Scale className="w-5 h-5 text-surface-600" />
              <h2 className="text-lg font-semibold text-surface-900">
                Tax Method Comparison
              </h2>
            </div>
            <div className={`p-2 rounded-xl transition-colors ${expandedSections.comparison ? 'bg-surface-100' : 'bg-surface-50'}`}>
              {expandedSections.comparison ? (
                <ChevronUp className="w-5 h-5 text-surface-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-surface-500" />
              )}
            </div>
          </button>

          {expandedSections.comparison && (
            <div className="mt-5 space-y-4">
              {/* Recommendation Banner */}
              <div className={`p-4 rounded-xl ${comparison.presumptiveBetter ? 'bg-income-100' : 'bg-amber-100'}`}>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className={`w-5 h-5 mt-0.5 ${comparison.presumptiveBetter ? 'text-income-600' : 'text-amber-600'}`} />
                  <div>
                    <p className={`font-semibold ${comparison.presumptiveBetter ? 'text-income-800' : 'text-amber-800'}`}>
                      {comparison.presumptiveBetter ? 'Presumptive Method (44ADA)' : 'Actual Expense Method'} is better for you
                    </p>
                    <p className={`text-sm mt-1 ${comparison.presumptiveBetter ? 'text-income-700' : 'text-amber-700'}`}>
                      You save {formatAmount(comparison.taxSavings)} in taxes compared to the other method
                    </p>
                  </div>
                </div>
              </div>

              {/* Method Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Presumptive Method Card */}
                <div
                  onClick={() => setSelectedMethod('presumptive')}
                  className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    (selectedMethod === 'presumptive' || (selectedMethod === 'recommended' && comparison.presumptiveBetter))
                      ? 'border-income-500 bg-income-50'
                      : 'border-surface-200 hover:border-surface-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-surface-200 rounded-lg text-xs font-medium text-surface-700">ITR-4</span>
                      <h3 className="font-semibold text-surface-900">Presumptive (44ADA)</h3>
                    </div>
                    {comparison.presumptiveBetter && (
                      <span className="px-2 py-1 bg-income-500 text-white rounded-lg text-xs font-medium">Recommended</span>
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-surface-600">Gross Income</span>
                      <span className="text-surface-900">{formatAmount(presumptiveMethod.grossIncome)}</span>
                    </div>
                    <div className="flex justify-between text-expense-600">
                      <span>Deemed Expenses (50%)</span>
                      <span>-{formatAmount(presumptiveMethod.deemedExpenses)}</span>
                    </div>
                    <div className="flex justify-between text-expense-600">
                      <span>Standard Deduction</span>
                      <span>-{formatAmount(presumptiveMethod.standardDeduction)}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t border-surface-200">
                      <span className="text-surface-900">Tax Payable</span>
                      <span className="text-expense-600">{formatAmount(presumptiveMethod.netTaxPayable)}</span>
                    </div>
                  </div>
                </div>

                {/* Actual Expense Method Card */}
                <div
                  onClick={() => setSelectedMethod('actual')}
                  className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                    (selectedMethod === 'actual' || (selectedMethod === 'recommended' && !comparison.presumptiveBetter))
                      ? 'border-income-500 bg-income-50'
                      : 'border-surface-200 hover:border-surface-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-surface-200 rounded-lg text-xs font-medium text-surface-700">ITR-3</span>
                      <h3 className="font-semibold text-surface-900">Actual Expenses</h3>
                    </div>
                    {!comparison.presumptiveBetter && (
                      <span className="px-2 py-1 bg-income-500 text-white rounded-lg text-xs font-medium">Recommended</span>
                    )}
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-surface-600">Gross Income</span>
                      <span className="text-surface-900">{formatAmount(actualMethod.grossIncome)}</span>
                    </div>
                    <div className="flex justify-between text-expense-600">
                      <span>Actual Expenses</span>
                      <span>-{formatAmount(actualMethod.actualExpenses)}</span>
                    </div>
                    <div className="flex justify-between text-expense-600">
                      <span>Standard Deduction</span>
                      <span>-{formatAmount(actualMethod.standardDeduction)}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-2 border-t border-surface-200">
                      <span className="text-surface-900">Tax Payable</span>
                      <span className="text-expense-600">{formatAmount(actualMethod.netTaxPayable)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-xs text-surface-500 p-3 bg-surface-50 rounded-xl">
                <strong>Note:</strong> Presumptive taxation (44ADA) assumes 50% of gross receipts as profit - simpler filing with ITR-4.
                Actual expense method requires maintaining books of accounts and filing ITR-3, but may be beneficial if your actual expenses exceed 50% of income.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-surface-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div
              className="p-2.5 rounded-xl"
              style={{ backgroundColor: 'var(--accent-bg)' }}
            >
              <TrendingUp className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
            </div>
            <span className="text-sm text-surface-500 font-medium">Gross Income</span>
          </div>
          <p className="text-2xl font-bold text-surface-900">
            {formatAmount(income.gross)}
          </p>
        </div>

        {hasExpenses && (
          <div className="bg-white rounded-2xl border border-surface-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 bg-expense-100 rounded-xl">
                <TrendingDown className="w-5 h-5 text-expense-600" />
              </div>
              <span className="text-sm text-surface-500 font-medium">Business Expenses</span>
            </div>
            <p className="text-2xl font-bold text-expense-600">
              {formatAmount(expenses.actual)}
            </p>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-surface-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-income-500 rounded-xl">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-surface-500 font-medium">TDS Paid</span>
          </div>
          <p className="text-2xl font-bold text-income-600">
            {formatAmount(summary.totalTDSPaid)}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-surface-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2.5 rounded-xl ${summary.refundDue > 0 ? 'bg-income-500' : 'bg-amber-500'}`}>
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <span className="text-sm text-surface-500 font-medium">
              {summary.refundDue > 0 ? 'Refund Due' : 'Tax Payable'}
            </span>
          </div>
          <p className={`text-2xl font-bold ${summary.refundDue > 0 ? 'text-income-600' : 'text-expense-600'}`}>
            {summary.refundDue > 0
              ? formatAmount(summary.refundDue)
              : formatAmount(summary.netTaxPayable)}
          </p>
        </div>
      </div>

      {/* Income Breakdown */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <button
          onClick={() => toggleSection('income')}
          className="w-full flex items-center justify-between"
        >
          <h2 className="text-lg font-semibold text-surface-900">
            Income Breakdown
          </h2>
          <div className={`p-2 rounded-xl transition-colors ${expandedSections.income ? 'bg-surface-100' : 'bg-surface-50'}`}>
            {expandedSections.income ? (
              <ChevronUp className="w-5 h-5 text-surface-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-surface-500" />
            )}
          </div>
        </button>

        {expandedSections.income && (
          <div className="space-y-4 mt-5">
            {/* Income by Category */}
            {income.categoryBreakdown.length > 0 && (
              <div className="overflow-x-auto rounded-xl border border-surface-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-50 text-left text-xs font-semibold text-surface-500 uppercase tracking-wider border-b border-surface-200">
                      <th className="px-4 py-4">Category</th>
                      <th className="px-4 py-4 text-right">Gross Amount</th>
                      <th className="px-4 py-4 text-right">TDS Deducted</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-100">
                    {income.categoryBreakdown.map((cat, index) => (
                      <tr key={index} className="hover:bg-surface-50 transition-colors">
                        <td className="px-4 py-4 text-surface-900 font-medium">{cat.name}</td>
                        <td className="px-4 py-4 text-right text-surface-900">
                          {formatAmount(cat.gross)}
                        </td>
                        <td className="px-4 py-4 text-right text-income-600">
                          {formatAmount(cat.tds)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-surface-50 font-semibold">
                      <td className="px-4 py-4 text-surface-900">Total</td>
                      <td className="px-4 py-4 text-right text-surface-900">
                        {formatAmount(income.gross)}
                      </td>
                      <td className="px-4 py-4 text-right text-income-600">
                        {formatAmount(summary.totalTDSPaid)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Tax Calculation Flow */}
            <div className="p-5 bg-surface-50 rounded-xl">
              <h3 className="font-semibold text-surface-900 mb-3">
                Tax Calculation ({currentMethod?.name || 'Recommended'})
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-surface-600">Gross Income</span>
                  <span className="font-medium text-surface-900">{formatAmount(currentMethod?.grossIncome || income.gross)}</span>
                </div>
                <div className="flex justify-between text-expense-600">
                  <span>Less: {comparison.presumptiveBetter || selectedMethod === 'presumptive' ? 'Deemed Expenses (50%)' : 'Actual Business Expenses'}</span>
                  <span>-{formatAmount(comparison.presumptiveBetter || selectedMethod === 'presumptive' ? currentMethod?.deemedExpenses : currentMethod?.actualExpenses)}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-surface-200">
                  <span className="text-surface-600">Taxable Profit</span>
                  <span className="font-medium text-surface-900">{formatAmount(currentMethod?.taxableProfit)}</span>
                </div>
                <div className="flex justify-between text-expense-600">
                  <span>Less: Standard Deduction</span>
                  <span>-{formatAmount(currentMethod?.standardDeduction)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-3 border-t border-surface-200 text-surface-900">
                  <span>Total Taxable Income</span>
                  <span>{formatAmount(currentMethod?.taxableIncome)}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Expense Breakdown (if has expenses) */}
      {hasExpenses && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <button
            onClick={() => toggleSection('expenses')}
            className="w-full flex items-center justify-between"
          >
            <h2 className="text-lg font-semibold text-surface-900">
              Business Expense Breakdown
            </h2>
            <div className={`p-2 rounded-xl transition-colors ${expandedSections.expenses ? 'bg-surface-100' : 'bg-surface-50'}`}>
              {expandedSections.expenses ? (
                <ChevronUp className="w-5 h-5 text-surface-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-surface-500" />
              )}
            </div>
          </button>

          {expandedSections.expenses && (
            <div className="space-y-4 mt-5">
              <div className="overflow-x-auto rounded-xl border border-surface-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-50 text-left text-xs font-semibold text-surface-500 uppercase tracking-wider border-b border-surface-200">
                      <th className="px-4 py-4">Expense Category</th>
                      <th className="px-4 py-4 text-center">Transactions</th>
                      <th className="px-4 py-4 text-right">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-100">
                    {expenses.categoryBreakdown.map((cat, index) => (
                      <tr key={index} className="hover:bg-surface-50 transition-colors">
                        <td className="px-4 py-4 text-surface-900 font-medium">{cat.name}</td>
                        <td className="px-4 py-4 text-center text-surface-600">{cat.count}</td>
                        <td className="px-4 py-4 text-right text-expense-600 font-medium">
                          {formatAmount(cat.total)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-surface-50 font-semibold">
                      <td className="px-4 py-4 text-surface-900">Total Business Expenses</td>
                      <td className="px-4 py-4 text-center text-surface-600">
                        {expenses.categoryBreakdown.reduce((acc, cat) => acc + cat.count, 0)}
                      </td>
                      <td className="px-4 py-4 text-right text-expense-600">
                        {formatAmount(expenses.actual)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Expense Comparison */}
              <div className="p-4 bg-amber-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-amber-800 font-medium">Expense Comparison</p>
                    <div className="mt-2 space-y-1 text-amber-700">
                      <p>Actual Expenses: {formatAmount(expenses.actual)} ({((expenses.actual / income.gross) * 100).toFixed(1)}% of income)</p>
                      <p>Presumptive Deemed Expenses: {formatAmount(expenses.presumptive)} (50% of income)</p>
                      {expenses.actual > expenses.presumptive ? (
                        <p className="font-medium text-income-700">
                          Your actual expenses are higher → Actual method saves you {formatAmount(comparison.taxSavings)}
                        </p>
                      ) : (
                        <p className="font-medium text-income-700">
                          Presumptive deemed expenses are higher → Presumptive method saves you {formatAmount(comparison.taxSavings)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tax Calculation */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <button
          onClick={() => toggleSection('tax')}
          className="w-full flex items-center justify-between"
        >
          <h2 className="text-lg font-semibold text-surface-900">
            Tax Calculation (New Regime FY 2025-26)
          </h2>
          <div className={`p-2 rounded-xl transition-colors ${expandedSections.tax ? 'bg-surface-100' : 'bg-surface-50'}`}>
            {expandedSections.tax ? (
              <ChevronUp className="w-5 h-5 text-surface-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-surface-500" />
            )}
          </div>
        </button>

        {expandedSections.tax && currentMethod && (
          <div className="space-y-4 mt-5">
            {/* Tax Slabs Table */}
            <div className="overflow-x-auto rounded-xl border border-surface-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-50 text-left text-xs font-semibold text-surface-500 uppercase tracking-wider border-b border-surface-200">
                    <th className="px-4 py-4">Income Slab</th>
                    <th className="px-4 py-4 text-center">Rate</th>
                    <th className="px-4 py-4 text-right">Amount in Slab</th>
                    <th className="px-4 py-4 text-right">Tax</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-100">
                  {currentMethod.tax.slabBreakdown.map((slab, index) => (
                    <tr key={index} className="hover:bg-surface-50 transition-colors">
                      <td className="px-4 py-4 text-surface-900">{slab.range}</td>
                      <td className="px-4 py-4 text-center text-surface-600">{slab.rate}</td>
                      <td className="px-4 py-4 text-right text-surface-900">
                        {formatAmount(slab.amount)}
                      </td>
                      <td className="px-4 py-4 text-right font-semibold text-surface-900">
                        {formatAmount(slab.tax)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Tax Summary */}
            <div className="p-5 bg-surface-50 rounded-xl space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-surface-600">Tax on Total Income</span>
                <span className="font-medium text-surface-900">{formatAmount(currentMethod.tax.baseTax)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-surface-600">Health & Education Cess (4%)</span>
                <span className="font-medium text-surface-900">{formatAmount(currentMethod.tax.cess)}</span>
              </div>
              <div className="flex justify-between font-semibold pt-3 border-t border-surface-200">
                <span className="text-surface-900">Total Tax Liability</span>
                <span className="text-expense-600">{formatAmount(currentMethod.tax.totalTax)}</span>
              </div>
              <div className="flex justify-between text-sm text-surface-500">
                <span>Effective Tax Rate</span>
                <span>{formatPercentage(currentMethod.tax.effectiveRate)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* TDS Summary */}
      <div className="bg-white rounded-2xl border border-surface-200 p-6">
        <button
          onClick={() => toggleSection('tds')}
          className="w-full flex items-center justify-between"
        >
          <h2 className="text-lg font-semibold text-surface-900">TDS Summary</h2>
          <div className={`p-2 rounded-xl transition-colors ${expandedSections.tds ? 'bg-surface-100' : 'bg-surface-50'}`}>
            {expandedSections.tds ? (
              <ChevronUp className="w-5 h-5 text-surface-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-surface-500" />
            )}
          </div>
        </button>

        {expandedSections.tds && (
          <div className="space-y-3 mt-5">
            {tds.breakdown.map((item, index) => (
              <div key={index} className="flex justify-between text-sm p-4 bg-surface-50 rounded-xl">
                <span className="text-surface-600">TDS on {item.category}</span>
                <span className="font-semibold text-income-600">
                  {formatAmount(item.tds)}
                </span>
              </div>
            ))}
            <div className="flex justify-between font-semibold p-4 bg-income-100 rounded-xl">
              <span className="text-income-900">Total TDS Paid</span>
              <span className="text-income-700">{formatAmount(tds.total)}</span>
            </div>
          </div>
        )}
      </div>

      {/* Advance Tax Schedule */}
      {advanceTax && (
        <div className="bg-white rounded-2xl border border-surface-200 p-6">
          <button
            onClick={() => toggleSection('advance')}
            className="w-full flex items-center justify-between"
          >
            <h2 className="text-lg font-semibold text-surface-900">
              Advance Tax Schedule
            </h2>
            <div className={`p-2 rounded-xl transition-colors ${expandedSections.advance ? 'bg-surface-100' : 'bg-surface-50'}`}>
              {expandedSections.advance ? (
                <ChevronUp className="w-5 h-5 text-surface-600" />
              ) : (
                <ChevronDown className="w-5 h-5 text-surface-500" />
              )}
            </div>
          </button>

          {expandedSections.advance && (
            <div className="mt-5">
              {!advanceTax.required ? (
                <div className="p-4 bg-income-100 rounded-xl">
                  <p className="text-income-800 font-medium">{advanceTax.message}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-amber-50 rounded-xl">
                    <p className="text-sm text-amber-800 font-medium">
                      <AlertCircle className="w-4 h-4 inline mr-2" />
                      Total Advance Tax Required: {formatAmount(advanceTax.totalAdvanceTax)}
                    </p>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-surface-200">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-surface-50 text-left text-xs font-semibold text-surface-500 uppercase tracking-wider border-b border-surface-200">
                          <th className="px-4 py-4">Quarter</th>
                          <th className="px-4 py-4">Due Date</th>
                          <th className="px-4 py-4 text-center">Cumulative %</th>
                          <th className="px-4 py-4 text-right">Installment</th>
                          <th className="px-4 py-4 text-right">Cumulative</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-surface-100">
                        {advanceTax.schedule.map((quarter) => (
                          <tr key={quarter.quarter} className="hover:bg-surface-50 transition-colors">
                            <td className="px-4 py-4 font-semibold text-surface-900">
                              {quarter.quarter}
                            </td>
                            <td className="px-4 py-4 text-surface-600">{quarter.dueDate}</td>
                            <td className="px-4 py-4 text-center text-surface-600">
                              {quarter.cumulativePercentage}
                            </td>
                            <td className="px-4 py-4 text-right text-surface-900">
                              {formatAmount(quarter.installmentAmount)}
                            </td>
                            <td className="px-4 py-4 text-right font-semibold text-surface-900">
                              {formatAmount(quarter.cumulativeAmount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Final Summary */}
      <div className={`rounded-2xl p-6 mb-4 ${summary.refundDue > 0 ? 'bg-income-100' : 'bg-expense-100'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-xl font-bold text-surface-900">
              {summary.refundDue > 0 ? 'Refund Expected' : 'Net Tax Payable'}
            </h3>
            <p className="text-sm text-surface-600 mt-1">
              After adjusting TDS • Using {summary.recommendedMethod === 'presumptive' ? 'Presumptive (44ADA)' : 'Actual Expense'} method • File {summary.itrForm}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className={`text-3xl font-bold ${summary.refundDue > 0 ? 'text-income-700' : 'text-expense-700'}`}>
              {summary.refundDue > 0
                ? formatAmount(summary.refundDue)
                : formatAmount(summary.netTaxPayable)}
            </p>
            <p className="text-sm text-surface-500 mt-2">
              Effective Rate: {formatPercentage(summary.effectiveRate)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
